import { Assessment } from '@hb/shared'

export interface TpalCounts {
  term: number
  preterm: number
  abortion: number
  living: number
}

export interface GravidaPara {
  gravida: number
  para: number
  tpal: TpalCounts
}

// GA at delivery below 259 days (37w 0d) counts as preterm
const PRETERM_CUTOFF_DAYS = 259

/**
 * Computes gravida/para + TPAL from the patient's other pregnancy assessments and the
 * additional pregnancies reported in their GYN history. The pregnancy identified by
 * `currentAssessmentId` is excluded from outcome counts and counted as the current
 * pregnancy (+1 gravida).
 */
export const getGravidaPara = (
  assessments: Record<string, Assessment> | null | undefined,
  currentAssessmentId: string | null | undefined,
  additionalPregnancies: Array<Record<string, unknown>>,
): GravidaPara => {
  const pastAssessments = Object.entries(assessments ?? {})
    .filter(([id]) => id !== currentAssessmentId)
    .map(([, a]) => a)

  let totalPast = 0
  let paraCount = 0
  let termCount = 0
  let pretermCount = 0
  let abortionCount = 0
  let livingCount = 0

  for (const a of pastAssessments) {
    totalPast++
    const outcome = (a.corrections?.pregnancyOutcome ?? a.data?.pregnancyOutcome) as
      | string
      | undefined
    if (outcome !== 'miscarriage' && outcome !== 'abortion') {
      paraCount++
      // GA at delivery determines term vs preterm; default to term if unknown
      const deliveryGa = a.corrections?.deliveryGa ?? (a.data?.deliveryGa as number | undefined)
      if (deliveryGa != null && deliveryGa < PRETERM_CUTOFF_DAYS) {
        pretermCount++
      } else {
        termCount++
      }
      livingCount++
    } else {
      abortionCount++
    }
  }

  for (const p of additionalPregnancies) {
    totalPast++
    const outcome = p.pregnancyOutcome as string | undefined
    if (
      outcome === 'born-living' ||
      outcome === 'born-with-complications' ||
      outcome === 'stillborn'
    ) {
      paraCount++
      termCount++
      if (outcome !== 'stillborn') livingCount++
    } else {
      abortionCount++
    }
  }

  return {
    gravida: totalPast + 1,
    para: paraCount,
    tpal: {
      term: termCount,
      preterm: pretermCount,
      abortion: abortionCount,
      living: livingCount,
    },
  }
}
